import { Orientation } from '@modules/src/enums/Orientation'
import Facade from '@/Facade'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'
import { ITickerVisualHandlers, TICKER_TEXT_NAME } from '@/managers/ui/TickerMessageSignalsManager'

export interface ITickerVisualConfig {
    backgroundTexture: string
    backgroundName: string
    electroTexture: string
    electroName: string
}

const TICKER_BUMP_SCALE = 1.06
const TICKER_BUMP_DURATION_SEC = 0.5
const TICKER_IDLE_BUMP_START_DELAY_SEC = 0.06
const TICKER_IDLE_BUMP_MAX_ATTEMPTS = 240

export const createTickerVisualHandlers = (
    resolveIdleMessage: () => string,
    visualConfig: ITickerVisualConfig
): ITickerVisualHandlers => {
    let tickerIdleBumpTween: gsap.core.Tween | null = null
    let tickerIdleBumpTarget: any | null = null
    let tickerIdleBumpStartTween: gsap.core.Tween | null = null

    const getTickerTextOffset = () => {
        const isLandscape = Facade.orientation !== Orientation.PORTRAIT
        if (isLandscape) {
            return { x: -2, y: 0 }
        }

        return { x: -2, y: 0 }
    }

    const stopTickerIdleBump = (container: any): void => {
        const textChild = container?.getChildByName(TICKER_TEXT_NAME) as any

        if (tickerIdleBumpTween) {
            tickerIdleBumpTween.kill()
            tickerIdleBumpTween = null
        }

        if (tickerIdleBumpStartTween) {
            tickerIdleBumpStartTween.kill()
            tickerIdleBumpStartTween = null
        }

        tickerIdleBumpTarget = null

        if (textChild && !textChild.destroyed) {
            gsap.killTweensOf(textChild.scale)
            textChild.scale.set(1)
        }
    }

    const bumpTickerText = (container: any): void => {
        const textChild = container?.getChildByName(TICKER_TEXT_NAME) as any

        if (!textChild || textChild.destroyed) {
            return
        }

        const textValue = typeof textChild.text === 'string' ? textChild.text.trim() : ''
        const shouldLoopBump = textValue === resolveIdleMessage()

        if (!shouldLoopBump) {
            stopTickerIdleBump(container)
            return
        }

        if (tickerIdleBumpTween && tickerIdleBumpTarget === textChild) {
            return
        }

        if (tickerIdleBumpTween) {
            tickerIdleBumpTween.kill()
        }

        tickerIdleBumpTarget = textChild
        textChild.scale.set(1)

        tickerIdleBumpTween = gsap.to(textChild.scale, {
            duration: TICKER_BUMP_DURATION_SEC,
            x: TICKER_BUMP_SCALE,
            y: TICKER_BUMP_SCALE,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
            overwrite: 'auto',
        })
    }

    const scheduleTickerIdleBump = (container: any, attempt = 0): void => {
        if (tickerIdleBumpStartTween) {
            tickerIdleBumpStartTween.kill()
            tickerIdleBumpStartTween = null
        }

        tickerIdleBumpStartTween = gsap.delayedCall(TICKER_IDLE_BUMP_START_DELAY_SEC, () => {
            tickerIdleBumpStartTween = null

            if (!container || container.destroyed) {
                return
            }

            const textChild = container?.getChildByName(TICKER_TEXT_NAME) as any
            const textValue = typeof textChild?.text === 'string' ? textChild.text.trim() : ''

            if (textValue !== resolveIdleMessage()) {
                if (attempt + 1 < TICKER_IDLE_BUMP_MAX_ATTEMPTS) {
                    scheduleTickerIdleBump(container, attempt + 1)
                }

                return
            }

            bumpTickerText(container)
        })
    }

    const ensureTickerDecorLayers = (container: any): void => {
        if (!container || container.destroyed) {
            return
        }

        container.sortableChildren = true

        let tickerBg = container.getChildByName(visualConfig.backgroundName) as any
        if (!tickerBg) {
            tickerBg = new PIXI.Sprite(PIXI.Texture.from(visualConfig.backgroundTexture))
            tickerBg.name = visualConfig.backgroundName
            tickerBg.anchor.set(0.5)
            tickerBg.position.set(0, 0)
            tickerBg.zIndex = -20
            container.addChild(tickerBg)
        }

        let tickerElectro = container.getChildByName(visualConfig.electroName) as any
        if (!tickerElectro) {
            tickerElectro = new PIXI.Sprite(PIXI.Texture.from(visualConfig.electroTexture))
            tickerElectro.name = visualConfig.electroName
            tickerElectro.anchor.set(0.5)
            tickerElectro.position.set(0, -6)
            tickerElectro.scale.set(0.96)
            tickerElectro.zIndex = -10
            container.addChild(tickerElectro)
        }

        const isLandscape = Facade.orientation !== Orientation.PORTRAIT
        if (isLandscape) {
            tickerBg.scale.set(0.7)
            tickerElectro.scale.set(0.7)
            tickerBg.position.set(149.5, 28)
            tickerElectro.position.set(145.5, 36)
        } else {
            tickerBg.scale.set(1)
            tickerElectro.scale.set(0.96)
            tickerBg.position.set(0, 0)
            tickerElectro.position.set(0, -6)
        }

        const textChild = container.getChildByName(TICKER_TEXT_NAME) as any
        if (!textChild || textChild.destroyed) {
            container.setChildIndex(tickerBg, 0)
            container.setChildIndex(tickerElectro, Math.min(1, container.children.length - 1))
            return
        }

        if (textChild.anchor?.set) {
            textChild.anchor.set(0.5)
        }

        const textOffset = getTickerTextOffset()
        const targetX = tickerBg.position.x + textOffset.x
        const targetY = tickerBg.position.y + textOffset.y

        textChild.position.set(targetX, targetY)

        const textValue = typeof textChild.text === 'string' ? textChild.text.trim() : ''
        if (textValue.length > 0) {
            const localBounds = textChild.getLocalBounds()
            const visualCenterY = localBounds.y + localBounds.height / 2
            textChild.position.set(targetX, targetY - visualCenterY)
        }

        textChild.zIndex = 1000
        container.setChildIndex(tickerBg, Math.max(0, container.getChildIndex(textChild) - 1))
        container.setChildIndex(tickerElectro, Math.max(0, container.getChildIndex(textChild) - 1))
        container.setChildIndex(textChild, container.children.length - 1)
    }

    const setTickerMessage = (container: any, message: string): boolean => {
        if (!container || container.destroyed) {
            return false
        }

        const textChild = container.getChildByName(TICKER_TEXT_NAME) as any
        if (!textChild || textChild.destroyed) {
            return false
        }

        textChild.text = message

        stopTickerIdleBump(container)
        container.visible = true
        ensureTickerDecorLayers(container)

        return true
    }

    return {
        scheduleTickerIdleBump,
        stopTickerIdleBump,
        ensureTickerDecorLayers,
        setTickerMessage,
    }
}